import React, { useRef, useState } from "react";
import {
    RASTER_PT,
    STANDARD_ABSTAND_PT,
    TAB_ARTEN,
    TAB_ART_NAME,
    type TabArt,
    type Tabstopp,
    cmText,
    rundePt,
} from "./tabstopps";

/**
 * TabLineal — das Lineal über der Schreibfläche, wie in Outlook.
 *
 * Bedienung wie in Word:
 *  - Links der Umschalter für die Art des nächsten Stopps (links, zentriert,
 *    rechts, am Komma).
 *  - Klick ins Lineal setzt einen Stopp (Raster 0,25 cm, mit Alt frei).
 *  - Einen Stopp ziehen verschiebt ihn; nach unten oder oben wegziehen oder
 *    doppelklicken entfernt ihn. Per Tastatur: Pfeiltasten bzw. Entf.
 *
 * Das Lineal zeigt die Stopps des Absatzes, in dem der Cursor steht, und
 * kennt den Editor nicht: Was ein Klick bewirkt, entscheidet der Aufrufer.
 * Jede Maustaste hier lässt den Fokus im Text (`preventDefault`), sonst ginge
 * die Auswahl verloren, auf die die Stopps wirken.
 */

const PT_ZU_PX = 4 / 3;
/** So weit muss man einen Stopp aus dem Lineal ziehen, damit er wegfällt. */
const WEGZIEH_PX = 16;
/** Wo im Zeichen die senkrechte Linie steht — dort liegt der Stopp. */
const ANKER_PX: Record<TabArt, number> = { links: 3.5, zentriert: 7.5, rechts: 11.5, dezimal: 7.5 };

export const StoppZeichen: React.FC<{ art: TabArt }> = ({ art }) => (
    <svg width="15" height="10" viewBox="0 0 15 10" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
        {art === "links" && <path d="M3.5 0.5V8.5H11" />}
        {art === "rechts" && <path d="M11.5 0.5V8.5H4" />}
        {art === "zentriert" && <path d="M7.5 0.5V8.5M3 8.5H12" />}
        {art === "dezimal" && (
            <>
                <path d="M7.5 0.5V8.5M3 8.5H12" />
                <circle cx="10.6" cy="4" r="1.1" fill="currentColor" stroke="none" />
            </>
        )}
    </svg>
);

export type TabLinealProps = {
    /** Breite des Schreibbereichs (ohne Innenabstand) in px. */
    breitePx: number;
    /** Wo der Schreibbereich beginnt, gemessen vom linken Rand des Lineals, in px. */
    versatzPx: number;
    /** Stopps des Absatzes am Cursor; `null`: Der Cursor steht nicht im Text. */
    stopps: Tabstopp[] | null;
    /** Einzug des Absatzes am Cursor in pt (nur Anzeige). */
    einzugPt: number;
    art: TabArt;
    onArt: (art: TabArt) => void;
    onSetzen: (stopp: Tabstopp) => void;
    onVerschieben: (altPt: number, neu: Tabstopp) => void;
    onEntfernen: (pt: number) => void;
};

type Zug = { startPt: number; art: TabArt; startX: number; startY: number; pt: number; weg: boolean };

export const TabLineal: React.FC<TabLinealProps> = ({
    breitePx,
    versatzPx,
    stopps,
    einzugPt,
    art,
    onArt,
    onSetzen,
    onVerschieben,
    onEntfernen,
}) => {
    const skalaRef = useRef<HTMLDivElement>(null);
    const [zug, setZug] = useState<Zug | null>(null);
    const breitePt = breitePx / PT_ZU_PX;
    const aktiv = stopps !== null;

    const imRaster = (pt: number, frei: boolean): number =>
        rundePt(Math.min(breitePt, Math.max(RASTER_PT, frei ? pt : Math.round(pt / RASTER_PT) * RASTER_PT)));

    const ptAusMaus = (clientX: number): number => {
        const links = skalaRef.current?.getBoundingClientRect().left ?? 0;
        return (clientX - links) / PT_ZU_PX;
    };

    const skalaKlick = (e: React.PointerEvent) => {
        if (!aktiv || e.button !== 0) return;
        e.preventDefault();
        const pt = ptAusMaus(e.clientX);
        if (pt <= 0 || pt > breitePt) return;
        onSetzen({ pt: imRaster(pt, e.altKey), art });
    };

    // ── Skala: Striche alle 0,25 cm, Zahlen jeden Zentimeter ──────────────
    const viertelPx = (RASTER_PT * PT_ZU_PX);
    const striche: React.ReactNode[] = [];
    for (let i = 1; i * viertelPx <= breitePx; i++) {
        const x = Math.round(i * viertelPx) + 0.5;
        if (i % 4 === 0) {
            striche.push(
                <text key={i} x={x} y={10} textAnchor="middle" className="rte-lineal-zahl">
                    {i / 4}
                </text>,
            );
        } else {
            const h = i % 2 === 0 ? 5 : 3;
            striche.push(<line key={i} x1={x} x2={x} y1={7 - h / 2} y2={7 + h / 2} />);
        }
    }

    // Standard-Stopps rechts vom letzten eigenen (links davon gelten sie nicht).
    const letzter = stopps?.length ? stopps[stopps.length - 1].pt : 0;
    const standard: React.ReactNode[] = [];
    if (aktiv) {
        for (let pt = Math.ceil((letzter + 0.01) / STANDARD_ABSTAND_PT) * STANDARD_ABSTAND_PT; pt <= breitePt; pt += STANDARD_ABSTAND_PT) {
            const x = Math.round(pt * PT_ZU_PX) + 0.5;
            standard.push(<line key={pt} x1={x} x2={x} y1={19} y2={22} className="rte-lineal-standard" />);
        }
    }

    const titel = aktiv
        ? "Klicken: Tabstopp setzen · Ziehen: verschieben · Wegziehen oder Doppelklick: entfernen"
        : "Für Tabstopps zuerst in den Text klicken";

    return (
        <div
            className={aktiv ? "rte-lineal" : "rte-lineal rte-lineal-aus"}
            role="group"
            aria-label="Lineal mit Tabstopps"
            onMouseDown={(e) => e.preventDefault()}
        >
            <button
                type="button"
                className="rte-lineal-art"
                style={{ width: Math.max(18, versatzPx - 2) }}
                title={`Art des nächsten Tabstopps: ${TAB_ART_NAME[art]} (klicken zum Wechseln)`}
                aria-label={`Art des nächsten Tabstopps: ${TAB_ART_NAME[art]}`}
                onClick={() => onArt(TAB_ARTEN[(TAB_ARTEN.indexOf(art) + 1) % TAB_ARTEN.length])}
            >
                <StoppZeichen art={art} />
            </button>
            <div
                ref={skalaRef}
                className="rte-lineal-skala"
                style={{ left: versatzPx, width: breitePx }}
                title={titel}
                onPointerDown={skalaKlick}
            >
                <svg className="rte-lineal-striche" width={breitePx} height={22} aria-hidden>
                    {striche}
                    {standard}
                    {aktiv && einzugPt > 0 && (
                        <path
                            className="rte-lineal-einzug"
                            d={`M${einzugPt * PT_ZU_PX} 16l-4 6h8z`}
                        />
                    )}
                </svg>
                {aktiv &&
                    // Schlüssel ist die Stelle in der Liste, nicht die Position: Sonst
                    // verlöre ein per Pfeiltaste verschobener Stopp den Fokus.
                    stopps!.map((s, i) => {
                        const ziehtGerade = zug?.startPt === s.pt;
                        const pt = ziehtGerade ? zug!.pt : s.pt;
                        return (
                            <button
                                key={i}
                                type="button"
                                className={ziehtGerade && zug!.weg ? "rte-lineal-stopp rte-lineal-weg" : "rte-lineal-stopp"}
                                style={{ left: pt * PT_ZU_PX - ANKER_PX[s.art] }}
                                title={`Tabstopp ${TAB_ART_NAME[s.art]} bei ${cmText(s.pt)} – ziehen zum Verschieben, wegziehen oder doppelklicken zum Entfernen`}
                                aria-label={`Tabstopp ${TAB_ART_NAME[s.art]} bei ${cmText(s.pt)}`}
                                onPointerDown={(e) => {
                                    if (e.button !== 0) return;
                                    e.stopPropagation();
                                    e.preventDefault();
                                    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
                                    setZug({ startPt: s.pt, art: s.art, startX: e.clientX, startY: e.clientY, pt: s.pt, weg: false });
                                }}
                                onPointerMove={(e) => {
                                    if (!zug || zug.startPt !== s.pt) return;
                                    const pt = imRaster(zug.startPt + (e.clientX - zug.startX) / PT_ZU_PX, e.altKey);
                                    setZug({ ...zug, pt, weg: Math.abs(e.clientY - zug.startY) > WEGZIEH_PX });
                                }}
                                onPointerUp={() => {
                                    if (!zug || zug.startPt !== s.pt) return;
                                    setZug(null);
                                    if (zug.weg) onEntfernen(zug.startPt);
                                    else if (Math.abs(zug.pt - zug.startPt) > 0.05) onVerschieben(zug.startPt, { pt: zug.pt, art: zug.art });
                                }}
                                onPointerCancel={() => setZug(null)}
                                onDoubleClick={() => onEntfernen(s.pt)}
                                onKeyDown={(e) => {
                                    if (e.key === "Delete" || e.key === "Backspace") {
                                        e.preventDefault();
                                        onEntfernen(s.pt);
                                    } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                                        e.preventDefault();
                                        const schritt = e.key === "ArrowLeft" ? -RASTER_PT : RASTER_PT;
                                        onVerschieben(s.pt, { pt: imRaster(s.pt + schritt, true), art: s.art });
                                    }
                                }}
                            >
                                <StoppZeichen art={s.art} />
                            </button>
                        );
                    })}
            </div>
        </div>
    );
};

export default TabLineal;
